import {
  AdvisorService,
  // requests
  GetAdvisorsOptions,
  GetSegmentsOptions,
  GetRiaGroupsPrivilegesOptions,
  CreateRiaUserOptions,
  UpdateRiaUserOptions,
  DeleteRiaUserOptions,
  PostSegmentsGoalOptions,
  PostSegmentsOptions,
  PostSegmentFeatureOptions,
  PostClientOnboardingOptions,
  DeleteSegmentsOptions,
  DeleteSegmentGoalOptions,
  GetWorkflowDocumentsOptions,
  GetAumTrendsOptions,
  GetRiaFirmOptions,
  RiaFirmOfficeRequest,
  // responses
  // RiaFirm, (commented out - API methods using these are disabled)
  RiaUser,
  RiaRoleAccessComponent,
  AdvisorFilter,
  RiaUserOverview,
  // RiaLabelSettings, (commented out - API methods using these are disabled)
  ClientOnboardingResponse,
  RiaGroupPrivilege,
  SegmentSetting,
  SegmentStatsResponse,
  SegmentThumbnailsResponse,
  SegmentFeaturesResponse,
  SegmentGoalResponse,
  WorkflowDocumentsResponse,
  AdvisorAumResponse,
  RiaFirmOffice,
  RiaUserNSK,
} from '@digital-advice/ms-advisor-sdk';
import {apiKey, getLogger, resolveEndpointForServiceToken, interceptors, sourceInfo} from '@advisor/services/sdk';

interface AdvisorServiceOptions {
  hasServiceTokenEndpoint?: boolean;
}

export const defaultOptions: AdvisorServiceOptions = {
  hasServiceTokenEndpoint: false,
};

export default class AdvisorServices {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static async sdkService(options: AdvisorServiceOptions = defaultOptions) {
    const service = await new AdvisorService({
      logger: getLogger(),
      apiKey,
      endpoint: resolveEndpointForServiceToken(options.hasServiceTokenEndpoint),
      interceptors,
      sourceInfo,
    });

    return service;
  }

  /**
   * Gateway endpoint: getRiaPrivateLabelSetting
   * @param {Object} {riaId}
   * @deprecated Replaced by hardcoded env config in src/config/riaSettings/
   */
  // static async getRiaPrivateLabelSetting({riaId}): Promise<RiaLabelSettings> {
  //   const serviceOptions: AdvisorServiceOptions = {
  //     hasServiceTokenEndpoint: true,
  //   };
  //   const advService = await this.sdkService(serviceOptions);

  //   return advService.getRiaPrivateLabelSetting({riaId});
  // }

  /**
   * Gateway endpoint: searchRiaFirmFromUrl
   * @param {String} riaLoginUrl
   * @deprecated Replaced by hardcoded env config in src/config/riaSettings/
   */
  // static async searchRiaFirmFromUrl(riaLoginUrl): Promise<RiaFirm[]> {
  //   const serviceOptions: AdvisorServiceOptions = {
  //     hasServiceTokenEndpoint: true,
  //   };
  //   const advService = await this.sdkService(serviceOptions);

  //   const result = await advService.searchRiaFirmFromUrl({riaLoginUrl});

  //   return result;
  // }

  /**
   * Gateway endpoint: getRiaFirm
   * @param {Object} {riaId}
   * @deprecated Replaced by hardcoded env config in src/config/riaSettings/
   */
  // static async getRiaFirm({riaId}): Promise<RiaFirm> {
  //   const serviceOptions: AdvisorServiceOptions = {
  //     hasServiceTokenEndpoint: true,
  //   };
  //   const advService = await this.sdkService(serviceOptions);

  //   return advService.getRiaFirm({riaId});
  // }

  /**
   * Gateway endpoint: getRiaFirmOffice
   * @param {Object} {riaId}
   */
  static async getRiaFirmOffice(payload: GetRiaFirmOptions): Promise<RiaFirmOffice> {
    const advService = await this.sdkService(defaultOptions);

    return advService.getRiaFirmOffice(payload);
  }

  /**
   * Gateway endpoint: postRiaFirmOffice
   * @param {Object} {riaId}
   */
  static async postRiaFirmOffice(payload: RiaFirmOfficeRequest): Promise<RiaFirmOffice> {
    const advService = await this.sdkService(defaultOptions);

    return advService.postRiaFirmOffice(payload);
  }

  /**
   * Gateway endpoint: postRiaFirmRiaId
   * @param {Object} {riaId}
   */
  static async postRiaFirmRiaId(payload: RiaFirmOfficeRequest): Promise<RiaFirmOffice> {
    const advService = await this.sdkService(defaultOptions);

    return advService.postRiaFirmRiaId(payload);
  }

  /**
   * Gateway endpoint: getRiaUserFromRiaUserId
   * @param {Object} {riaId}
   */
  static async getRiaUserFromRiaUserId({riaUserId}): Promise<RiaUser> {
    const serviceOptions: AdvisorServiceOptions = {
      hasServiceTokenEndpoint: true,
    };
    const advService = await this.sdkService(serviceOptions);

    return advService.getRiaUserFromRiaUserId({riaUserId});
  }

  /**
   * Gateway endpoint: getRiaRoleAccessComponents
   * @param {Object} {}
   */
  static async getRiaRoleAccessComponents({
    riaUserId,
    riaGroupId,
    isSidebarMenu,
    isModule,
  }): Promise<RiaRoleAccessComponent> {
    const advService = await this.sdkService();

    return advService.getRiaRoleAccessComponents({
      riaUserId,
      riaGroupId,
      isSidebarMenu,
      isModule,
    });
  }

  /**
   * Gateway endpoint: getAdvisorFilters
   * @param {Object} {filterGroupId}
   */
  static async getAdvisorFilters({filterGroupId}): Promise<AdvisorFilter> {
    const advService = await this.sdkService();

    return advService.getAdvisorFilters({filterGroupId});
  }

  /**
   * Gateway endpoint: getRiaUserOverview
   * @param {Object} {riaUserId}
   */
  static async getRiaUserOverview({riaUserId}): Promise<RiaUserOverview> {
    const advService = await this.sdkService();

    return advService.getRiaUserOverview({riaUserId});
  }

  /**
   * Gateway endpoint: updateNSKRiaUser
   */
  static async updateNSKRiaUser(payload): Promise<RiaUserNSK[]> {
    const advService = await this.sdkService();
    const data = {idToken: payload};

    return advService.updateNSKRiaUser({idToken: data});
  }

  /**
   * Gateway endpoint: getAdvisors
   * @param {GetAdvisorsOptions} payload
   */
  static async getAdvisors(payload: GetAdvisorsOptions): Promise<RiaUser[]> {
    const advService = await this.sdkService();

    return advService.getAdvisors(payload);
  }

  /**
   * Gateway endpoint: getOnboardingData
   * @param {GetSegmentsOptions} payload
   */
  static async getOnboardingData(payload: GetSegmentsOptions): Promise<ClientOnboardingResponse> {
    const advService = await this.sdkService();

    return advService.getOnboardingData(payload);
  }

  /**
   * Gateway endpoint: getRiaGroupsPrivileges
   * @param {GetRiaGroupsPrivilegesOptions} payload
   */
  static async getRiaGroupsPrivileges(payload: GetRiaGroupsPrivilegesOptions): Promise<RiaGroupPrivilege[]> {
    const advService = await this.sdkService();

    return advService.getRiaGroupsPrivileges(payload);
  }

  /**
   * Gateway endpoint: createRiaUser
   * @param {CreateRiaUserOptions} payload
   */
  static async createRiaUser(payload: CreateRiaUserOptions): Promise<RiaUser> {
    const advService = await this.sdkService();

    return advService.createRiaUser(payload);
  }

  /**
   * Gateway endpoint: createRiaUser
   * @param {UpdateRiaUserOptions} payload
   */
  static async updateRiaUser(payload: UpdateRiaUserOptions): Promise<RiaUser> {
    const advService = await this.sdkService();

    return advService.updateRiaUser(payload);
  }

  /**
   * Gateway endpoint: deleteRiaUser
   * @param {DeleteRiaUserOptions} payload
   */
  static async deleteRiaUser(payload: DeleteRiaUserOptions): Promise<void> {
    const advService = await this.sdkService();

    return advService.deleteRiaUser(payload);
  }

  /**
   * Gateway endpoint: getSegments
   * @param {GetSegmentsOptions} payload
   */
  static async getSegments(payload: GetSegmentsOptions): Promise<SegmentSetting[]> {
    const advService = await this.sdkService();

    return advService.getSegments(payload);
  }

  /**
   * Gateway endpoint: getSegment
   * @param {GetSegmentsOptions} payload
   */
  static async getSegment(payload: GetSegmentsOptions): Promise<SegmentSetting> {
    const advService = await this.sdkService();

    return advService.getSegment(payload);
  }

  /**
   * Gateway endpoint: getSegmentStats
   * @param {GetSegmentsOptions} payload
   */
  static async getSegmentStats(payload: GetSegmentsOptions): Promise<SegmentStatsResponse> {
    const advService = await this.sdkService();

    return advService.getSegmentStats(payload);
  }

  /**
   * Gateway endpoint: getSegmentThumbnails
   * @param {GetSegmentsOptions} payload
   */
  static async getSegmentThumbnails(payload: GetSegmentsOptions): Promise<SegmentThumbnailsResponse[]> {
    const advService = await this.sdkService();

    return advService.getSegmentThumbnails(payload);
  }

  /**
   * Gateway endpoint: getSegmentGoals
   * @param {GetSegmentsOptions} payload
   */
  // TODO: review SDK
  static async getSegmentGoals(payload: GetSegmentsOptions): Promise<SegmentGoalResponse[]> {
    const advService = await this.sdkService();

    return advService.getSegmentGoals(payload);
  }

  /**
   * Gateway endpoint: getSegmentFeatures
   * @param {GetSegmentsOptions} payload
   */
  static async getSegmentFeatures(payload: GetSegmentsOptions): Promise<SegmentFeaturesResponse[]> {
    const advService = await this.sdkService();

    return advService.getSegmentFeatures(payload);
  }

  /**
   * Gateway endpoint: createUpdateSegmentGoal
   * @param {PostSegmentsGoalOptions} payload
   */
  // TODO: review SDK
  static async createUpdateSegmentGoal(payload: PostSegmentsGoalOptions): Promise<SegmentGoalResponse> {
    const advService = await this.sdkService();

    return advService.createUpdateSegmentGoal(payload);
  }

  /**
   * Gateway endpoint: createCloneSegment
   * @param {PostSegmentsOptions} payload
   */
  static async createCloneSegment(payload: PostSegmentsOptions): Promise<SegmentSetting> {
    const advService = await this.sdkService();

    return advService.createCloneSegment(payload);
  }

  /**
   * Gateway endpoint: updateSegment
   * @param {PostSegmentsOptions} payload
   */
  static async updateSegment(payload: PostSegmentsOptions): Promise<SegmentSetting> {
    const advService = await this.sdkService();

    return advService.updateSegment(payload);
  }

  /**
   * Gateway endpoint: updateSegmentFeatureStatus
   * @param {PostSegmentFeatureOptions} payload
   */
  static async updateSegmentFeatureStatus(payload: PostSegmentFeatureOptions): Promise<SegmentFeaturesResponse[]> {
    const advService = await this.sdkService();

    return advService.updateSegmentFeatureStatus(payload);
  }

  /**
   * Gateway endpoint: createUpdateOnboardingData
   * @param {PostClientOnboardingOptions} payload
   */
  static async createUpdateOnboardingData(payload: PostClientOnboardingOptions): Promise<ClientOnboardingResponse> {
    const advService = await this.sdkService();

    return advService.createUpdateOnboardingData(payload);
  }

  /**
   * Gateway endpoint: deleteSegment
   * @param {DeleteSegmentsOptions} payload
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async deleteSegment(payload: DeleteSegmentsOptions): Promise<any> {
    const advService = await this.sdkService();

    return advService.deleteSegment(payload);
  }

  /**
   * Gateway endpoint: deleteSegmentGoal
   * @param {DeleteSegmentGoalOptions} payload
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async deleteSegmentGoal(payload: DeleteSegmentGoalOptions): Promise<any> {
    const advService = await this.sdkService();

    return advService.deleteSegmentGoal(payload);
  }

  /**
   * Gateway endpoint: getWorkflowDocuments
   * @param {GetWorkflowDocumentsOptions} payload
   */
  static async getWorkflowDocuments(payload: GetWorkflowDocumentsOptions): Promise<WorkflowDocumentsResponse> {
    const advService = await this.sdkService();

    return advService.getWorkflowDocuments(payload);
  }

  /**
   * Gateway endpoint: getAdvisorAumTrends
   * @param {GetAumTrendsOptions} payload
   */
  static async getAdvisorAumTrends(payload: GetAumTrendsOptions): Promise<AdvisorAumResponse> {
    const advService = await this.sdkService();

    return advService.getAdvisorAumTrends(payload);
  }
}
